const rawBaseUrl = process.env.REACT_APP_API_BASE_URL;

function normalizeLocalDevBaseUrl(value) {
  if (!value || typeof window === 'undefined') return value;
  if (window.location.hostname !== 'localhost') return value;

  try {
    const parsed = new URL(value);
    if (parsed.hostname !== 'localhost') {
      parsed.hostname = 'localhost';
      return parsed.toString().replace(/\/+$/, '');
    }
  } catch (_error) {
    return value;
  }

  return value;
}

function resolveBaseUrl() {
  const fallback = '/api/public';
  const trimmed = normalizeLocalDevBaseUrl(rawBaseUrl || fallback).replace(/\/+$/, '');

  // Si el usuario configuró solo el dominio (ej: https://mi-backend.com)
  // agregamos automáticamente el segmento /api/public que exige el backend.
  if (trimmed.endsWith('/api/public')) return trimmed;
  if (trimmed.endsWith('/api')) return `${trimmed}/public`;
  return `${trimmed}/api/public`;
}

const BASE_URL = resolveBaseUrl();
const AUTH_BASE_URL = BASE_URL.replace(/\/public$/, '/auth');
let currentShopSlug = null;

function resolveApiOrigin() {
  if (/^https?:\/\//i.test(BASE_URL)) {
    return BASE_URL.replace(/\/api\/public$/, '');
  }

  return 'https://api.barberappbycodex.com';
}

function resolveApiMediaUrl(value) {
  if (typeof value !== 'string' || !value.startsWith('/api/public/media/')) {
    return value;
  }

  return `${resolveApiOrigin()}${value}`;
}

// Pide la foto del servidor ya achicada (el backend la devuelve en WebP con
// ?w=<ancho>). Los originales pesan hasta 2 MB y se muestran en 100-700 px.
// Si la URL no es de /api/public/media (data URL, otra fuente), no se toca;
// y un servidor viejo que no conoce ?w= devuelve el original, sin romper.
export function sizedMediaUrl(value, width) {
  if (typeof value !== 'string' || !value.includes('/api/public/media/')) {
    return value;
  }
  return `${value}${value.includes('?') ? '&' : '?'}w=${width}`;
}

function normalizeThemeMediaUrls(payload) {
  const themeConfig = payload?.shop?.themeConfig || payload?.user?.themeConfig;
  if (themeConfig) {
    themeConfig.logoDataUrl = resolveApiMediaUrl(themeConfig.logoDataUrl);
    themeConfig.bannerDataUrl = resolveApiMediaUrl(themeConfig.bannerDataUrl);
    themeConfig.mobileBannerDataUrl = resolveApiMediaUrl(
      themeConfig.mobileBannerDataUrl,
    );
  }

  // Fotos de barberos: ahora se guardan como archivo (antes iban en base64
  // adentro del JSON), así que la ruta relativa hay que resolverla contra
  // el origen de la API.
  if (Array.isArray(payload?.barbers)) {
    payload.barbers.forEach((b) => {
      // Algunas respuestas traen el barbero como ID suelto (string), no
      // como objeto populado -- sin este chequeo de tipo, poner .photoUrl
      // ahí explota.
      if (b && typeof b === 'object') b.photoUrl = resolveApiMediaUrl(b.photoUrl);
    });
  }
  if (payload?.barber && typeof payload.barber === 'object') {
    payload.barber.photoUrl = resolveApiMediaUrl(payload.barber.photoUrl);
  }
  // Fotos de servicios (mismo esquema que las de barberos).
  if (Array.isArray(payload?.services)) {
    payload.services.forEach((s) => {
      if (s && typeof s === 'object') s.photoUrl = resolveApiMediaUrl(s.photoUrl);
    });
  }
  // Fotos de productos de la tienda.
  if (Array.isArray(payload?.products)) {
    payload.products.forEach((p) => {
      if (p && typeof p === 'object') p.photoUrl = resolveApiMediaUrl(p.photoUrl);
    });
  }

  return payload;
}

function sanitizeSlug(value) {
  return (
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '') || null
  );
}

export function setShopSlug(slug) {
  currentShopSlug = sanitizeSlug(slug);
}

export function getShopSlug() {
  return currentShopSlug;
}

function buildShopPath(path = '') {
  if (!currentShopSlug) {
    throw new Error('No se configuró la barbería actual.');
  }
  const suffix = path ? (path.startsWith('/') ? path : `/${path}`) : '';
  return `/shops/${currentShopSlug}${suffix}`;
}

async function request(path, options = {}) {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  };

  if (config.body && typeof config.body !== 'string') {
    config.body = JSON.stringify(config.body);
  }

  const response = await fetch(url, config);
  let payload = null;

  try {
    payload = await response.json();
  } catch (err) {
    // Ignore JSON parse errors for empty responses.
  }

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      `La solicitud falló con código ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.details = payload;
    throw error;
  }

  return normalizeThemeMediaUrls(payload);
}

export async function fetchBarbers() {
  return request(buildShopPath('/barbers'));
}

export async function fetchServices() {
  return request(buildShopPath('/services'));
}

// ¿El email tiene una membresía vigente con saldo en este local?
// `serviceIds` (opcional): los servicios elegidos, para saber si la membresía los cubre.
export async function fetchPublicMembership(email, serviceIds = []) {
  const q = encodeURIComponent(email || '');
  const ids = Array.isArray(serviceIds) && serviceIds.length ? `&serviceIds=${encodeURIComponent(serviceIds.join(','))}` : '';
  return request(buildShopPath(`/membership?email=${q}${ids}`));
}

export async function validateBookingCoupon(payload) {
  return request(buildShopPath('/coupons/validate'), {
    method: 'POST',
    body: payload,
  });
}

// Turnos privados: valida el código sin crear turno. Devuelve { required, valid }.
export async function verifyBookingCode(code) {
  return request(buildShopPath('/booking/verify-code'), {
    method: 'POST',
    body: { code },
  });
}

// En tu api.js
export async function fetchBarberAppointments(barberId, date) {
  // Verificá que date sea "YYYY-MM-DD"
  const query = date ? `?date=${date}` : '';
  return request(buildShopPath(`/barbers/${barberId}/appointments${query}`));
}

export async function createAppointment(payload) {
  return request(buildShopPath('/appointments'), {
    method: 'POST',
    body: payload,
  });
}

// Pago por alias: el cliente avisa que ya transfirió (la clave la recibió al reservar).
export async function reportAliasTransfer({ appointmentId, claimToken }) {
  return request(buildShopPath(`/appointments/${encodeURIComponent(appointmentId)}/transfer-sent`), {
    method: 'POST',
    body: { claimToken },
  });
}

// Tienda de productos del local (retiro en el local).
export async function fetchStoreProducts() {
  return request(buildShopPath('/products'));
}

export async function createStoreOrder(payload) {
  return request(buildShopPath('/orders'), {
    method: 'POST',
    body: payload,
  });
}

// Ticket de un pedido (se pide con el código y la clave que recibe el cliente).
export async function fetchStoreTicket({ code, token }) {
  return request(
    `/orders/ticket?code=${encodeURIComponent(code)}&t=${encodeURIComponent(token)}`,
  );
}

// Pedido pagado por alias: el cliente avisa que ya transfirió.
export async function reportStoreAliasTransfer({ code, token }) {
  return request('/orders/transfer-sent', {
    method: 'POST',
    body: { code, t: token },
  });
}

export function storeTicketPdfUrl({ code, token }) {
  return `${BASE_URL}/orders/ticket.pdf?code=${encodeURIComponent(code)}&t=${encodeURIComponent(token)}`;
}

// Compra de membresías desde la web de turnos.
export async function fetchMembershipPlans() {
  return request(buildShopPath('/membership-plans'));
}

export async function createMembershipPurchase(payload) {
  return request(buildShopPath('/membership-purchases'), {
    method: 'POST',
    body: payload,
  });
}

export async function fetchMembershipTicket({ code, token }) {
  return request(
    `/membership-purchases/ticket?code=${encodeURIComponent(code)}&t=${encodeURIComponent(token)}`,
  );
}

// Alias: el cliente avisa que ya transfirió.
export async function reportMembershipAliasTransfer({ code, token }) {
  return request('/membership-purchases/transfer-sent', {
    method: 'POST',
    body: { code, t: token },
  });
}

export function membershipTicketPdfUrl({ code, token }) {
  return `${BASE_URL}/membership-purchases/ticket.pdf?code=${encodeURIComponent(code)}&t=${encodeURIComponent(token)}`;
}

export async function createWaitlistEntry(payload) {
  return request(buildShopPath('/waitlist'), {
    method: 'POST',
    body: payload,
  });
}

// Lista de espera: lo que trae el link "se liberó un turno" del mail, y la
// confirmación en un toque (el primero que confirma se queda con el turno).
export async function fetchWaitlistOffer(token) {
  return request(`/waitlist-offers/${encodeURIComponent(token)}`);
}

export async function claimWaitlistOffer(token) {
  return request(`/waitlist-offers/${encodeURIComponent(token)}/claim`, { method: 'POST' });
}

export async function fetchShopInfo() {
  return request(buildShopPath());
}

export async function fetchShopMedia() {
  return request(buildShopPath('/media'));
}

export async function fetchPlanPricing() {
  return request('/plans');
}

// Lookup de solo lectura de un cupón (para mostrar qué ofrece en /registro).
// No valida contra un plan ni cuenta como canje.
export async function fetchSubscriptionCouponPreview(code) {
  return request(`/subscriptions/coupon-preview?code=${encodeURIComponent(code)}`);
}

export async function createPublicSubscriptionCheckout(payload) {
  return request('/subscriptions/checkout', {
    method: 'POST',
    body: payload,
  });
}

export async function createPublicSubscriptionPayment(payload) {
  return request('/subscriptions/payment', {
    method: 'POST',
    body: payload,
  });
}

// Sumar sucursal(es) a una cuenta ya suscripta (cobro único con tarjeta).
export async function addBranchesPayment(payload) {
  return request('/subscriptions/add-branches', {
    method: 'POST',
    body: payload,
  });
}

export async function fetchPublicSubscriptionQuote(payload) {
  return request('/subscriptions/quote', {
    method: 'POST',
    body: payload,
  });
}

export async function createPublicRecurringSubscription(payload) {
  return request('/subscriptions/recurring/start', {
    method: 'POST',
    body: payload,
  });
}

async function postAuthJson(path, body) {
  const response = await fetch(`${AUTH_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(
      data?.message || data?.error || `La solicitud falló con código ${response.status}`,
    );
    error.status = response.status;
    error.details = data;
    throw error;
  }
  return data;
}

// Confirmación del mail con el código de 6 números (cuentas nuevas).
export async function verifyPublicEmailCode({ email, code }) {
  return postAuthJson('/email/verify', { email, code });
}

export async function resendPublicEmailCode(email) {
  return postAuthJson('/email/resend', { email });
}

export async function registerPublicAccount(payload) {
  const response = await fetch(`${AUTH_BASE_URL}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...payload,
      registrationSource: 'web',
    }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      data?.message ||
      data?.error ||
      `La solicitud falló con código ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.details = data;
    throw error;
  }

  return data;
}

// Preguntas frecuentes de la landing (las carga el admin). Si falla, [].
export async function fetchFaqs() {
  try {
    const response = await fetch(`${BASE_URL}/faqs`);
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data?.faqs) ? data.faqs : [];
  } catch (_error) {
    return [];
  }
}

// Consulta que deja un visitante cuando no encuentra su respuesta.
export async function sendFaqInquiry({ name, contact, message, website = "" }) {
  const response = await fetch(`${BASE_URL}/faqs/inquiries`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, contact, message, website }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.error || "No pudimos enviar tu consulta. Probá de nuevo.");
  }
  return data;
}

// Vitrina de la landing: barberías activas con logo y portada. Las rutas de
// media vienen relativas (/api/public/media/...) y acá se les antepone el
// origen de la API. Si falla, devuelve [] y la landing muestra su fallback.
// Alianza Codex × Vidalac: si se muestra la página /alianza y sus datos.
export async function fetchPublicAlliance() {
  const response = await fetch(`${BASE_URL}/alliance`);
  if (!response.ok) throw new Error('No disponible');
  return response.json();
}

export async function fetchShowcase() {
  try {
    const response = await fetch(`${BASE_URL}/showcase`);
    if (!response.ok) return [];
    const data = await response.json();
    // En desarrollo (localhost) el backend local usa la base real pero no
    // tiene los archivos de /uploads en el disco → las fotos se piden al
    // API de producción para poder ver la vitrina real. En producción,
    // origen normal de la API.
    const isLocalDev = typeof window !== "undefined" && window.location.hostname === "localhost";
    const mediaUrl = (value) =>
      isLocalDev && typeof value === "string" && value.startsWith("/api/public/media/")
        ? `https://api.barberappbycodex.com${value}`
        : resolveApiMediaUrl(value);
    return (data?.shops || []).map((s) => ({
      ...s,
      logo: sizedMediaUrl(mediaUrl(s.logo), 160),
      banner: sizedMediaUrl(mediaUrl(s.banner), 768),
    }));
  } catch (_error) {
    return [];
  }
}

// Avisa al servidor que una pantalla de la web falló (ErrorBoundary), para
// verlo en el monitoreo del panel admin. Nunca lanza.
export function reportClientError(payload) {
  try {
    return fetch(`${BASE_URL}/client-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => null);
  } catch (_error) {
    return Promise.resolve(null);
  }
}

// ---------------------------------------------------------------------------
// Academia (público y alumno). El alumno entra con un código por mail; el token
// se guarda en el navegador.
// ---------------------------------------------------------------------------
const STUDENT_TOKEN_KEY = 'academy_student_token';
export function getStudentToken() {
  try {
    return localStorage.getItem(STUDENT_TOKEN_KEY) || '';
  } catch (_e) {
    return '';
  }
}
export function setStudentToken(token) {
  try {
    if (token) localStorage.setItem(STUDENT_TOKEN_KEY, token);
    else localStorage.removeItem(STUDENT_TOKEN_KEY);
  } catch (_e) {
    /* navegador sin storage */
  }
}
const ACADEMY_ROOT = BASE_URL.replace(/\/public$/, '') + '/academy';
async function academyRequest(path, options = {}) {
  const token = getStudentToken();
  const config = {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(options.headers || {}) },
  };
  if (config.body && typeof config.body !== 'string') config.body = JSON.stringify(config.body);
  const response = await fetch(`${ACADEMY_ROOT}${path}`, config);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(payload?.error || `La solicitud falló con código ${response.status}`);
    error.status = response.status;
    error.details = payload;
    throw error;
  }
  return payload;
}
// Las portadas de cursos y de la página llegan como /api/public/media/...: se
// resuelven contra el origen de la API (si no, la imagen se rompe).
export function resolveMediaUrl(value) {
  return resolveApiMediaUrl(value);
}
export async function fetchShopCourses(slug) {
  const res = await request(`/shops/${encodeURIComponent(slug)}/academy/courses`);
  if (res?.profile) res.profile.coverUrl = resolveApiMediaUrl(res.profile.coverUrl);
  (res?.courses || []).forEach((c) => {
    c.coverUrl = resolveApiMediaUrl(c.coverUrl);
  });
  return res;
}
export async function fetchShopCourse(slug, courseSlug) {
  const res = await request(`/shops/${encodeURIComponent(slug)}/academy/courses/${encodeURIComponent(courseSlug)}`);
  if (res?.course) res.course.coverUrl = resolveApiMediaUrl(res.course.coverUrl);
  return res;
}
export function fetchCoursePreviewLesson(slug, courseSlug, lessonId) {
  return request(`/shops/${encodeURIComponent(slug)}/academy/courses/${encodeURIComponent(courseSlug)}/preview/${lessonId}`);
}
export function enrollInCourse(slug, courseSlug, body) {
  return request(`/shops/${encodeURIComponent(slug)}/academy/courses/${encodeURIComponent(courseSlug)}/enroll`, { method: 'POST', body });
}
export function studentRequestCode(email) {
  return academyRequest('/student/request-code', { method: 'POST', body: { email } });
}
export function studentLogin(email, code) {
  return academyRequest('/student/login', { method: 'POST', body: { email, code } });
}
export async function fetchStudentCourses() {
  const res = await academyRequest('/student/courses');
  (res?.courses || []).forEach((c) => {
    if (c.course) c.course.coverUrl = resolveApiMediaUrl(c.course.coverUrl);
  });
  return res;
}
export function fetchStudentCourse(enrollmentId) {
  return academyRequest(`/student/courses/${enrollmentId}`);
}
export function markStudentLesson(enrollmentId, lessonId, done = true) {
  return academyRequest(`/student/courses/${enrollmentId}/progress`, { method: 'POST', body: { lessonId, done } });
}
// El PDF se baja con la sesión del alumno (cabecera Authorization) y se muestra como blob:
// así no depende de que el navegador deje embeber el dominio de la API y el token no viaja en la URL.
export async function fetchStudentLessonPdfBlob(enrollmentId, lessonId) {
  const token = getStudentToken();
  const response = await fetch(`${ACADEMY_ROOT}/student/courses/${enrollmentId}/lessons/${lessonId}/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const error = new Error(payload?.error || 'No pudimos abrir el PDF.');
    error.status = response.status;
    throw error;
  }
  const blob = await response.blob();
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
}
